/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { dateFormate } from "util/Util";
import {
  CalendarDays,
  ArrowRight,
  AlertTriangle,
  Wallet,
  Briefcase,
  UserCheck,
  Clock,
  Tag,
} from "lucide-react";

interface IProps {
  selectedRow: any;
}

// Trim trailing zeros: 1.00 → "1", 0.5 → "0.5".
const formatDays = (value: number | null | undefined): string =>
  String(parseFloat((Number(value) || 0).toFixed(2)));

const getInitials = (name?: string): string => {
  if (!name?.trim()) return "–";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
};

const statusClasses = (status: string): string => {
  switch ((status || "").toLowerCase()) {
    case "approved":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/30 dark:text-emerald-400";
    case "submitted":
    case "pending":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/30 dark:text-amber-400";
    case "rejected":
      return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/30 dark:text-rose-400";
    default:
      return "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300";
  }
};

// Compact label/value block used across the detail grid.
const InfoItem: React.FC<{
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ label, value, icon }) => (
  <div className="min-w-0">
    <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
      {icon}
      {label}
    </p>
    <p className="mt-0.5 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
      {value ?? "—"}
    </p>
  </div>
);

const ViewLeave = ({ selectedRow }: IProps) => {
  const unit = Number(selectedRow?.unit) || 0;
  const lopDays = Number(selectedRow?.lopDays) || 0;
  const hasBalance = selectedRow?.currentBalance != null;
  const durationLabel = unit === 0.5 ? "Half day" : `${formatDays(unit)} ${unit === 1 ? "day" : "days"}`;
  const warning: string | null = selectedRow?.balanceWarning ?? null;

  return (
    <div className="bg-white px-1 pb-1 dark:bg-gray-800">
      {/* Header — employee identity + status */}
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4 dark:border-slate-700/70">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
            {getInitials(selectedRow?.username)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-slate-900 dark:text-white">
              {selectedRow?.username || "—"}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {selectedRow?.email || "—"}
            </p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses(
            selectedRow?.status,
          )}`}
        >
          {selectedRow?.status || "—"}
        </span>
      </div>

      {/* Balance warning (when present) */}
      {warning && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{warning}</span>
        </div>
      )}

      {/* Detail grid */}
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4">
        {/* From → To rendered as a single key/value pair (replaces the banner). */}
        <InfoItem
          label="Period"
          icon={<CalendarDays className="h-3 w-3" />}
          value={
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span>{dateFormate(selectedRow?.leaveFrom) || "—"}</span>
              <ArrowRight className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
              <span>{dateFormate(selectedRow?.leaveTo) || "—"}</span>
            </span>
          }
        />
        <InfoItem
          label="Duration"
          icon={<Clock className="h-3 w-3" />}
          value={durationLabel}
        />
        <InfoItem
          label="Leave Type"
          icon={<Tag className="h-3 w-3" />}
          value={selectedRow?.leaveType || "—"}
        />

        <InfoItem
          label="Applied On"
          icon={<Clock className="h-3 w-3" />}
          value={selectedRow?.leaveAppliedDate ? dateFormate(selectedRow.leaveAppliedDate) : "—"}
        />
        <InfoItem
          label="Department"
          icon={<Briefcase className="h-3 w-3" />}
          value={<span className="capitalize">{selectedRow?.department || "—"}</span>}
        />
        {selectedRow?.adminEmail && (
          <InfoItem
            label="Approved By"
            icon={<UserCheck className="h-3 w-3" />}
            value={selectedRow.adminEmail}
          />
        )}
        {hasBalance && (
          <InfoItem
            label="Current Balance"
            icon={<Wallet className="h-3 w-3" />}
            value={`${formatDays(selectedRow.currentBalance)} days`}
          />
        )}
        {lopDays > 0 && (
          <InfoItem
            label="Loss of Pay"
            value={
              <span className="text-rose-600 dark:text-rose-400">
                {formatDays(lopDays)} {lopDays === 1 ? "day" : "days"}
              </span>
            }
          />
        )}
        {(lopDays > 0 || selectedRow?.isDeductible) && (
          <InfoItem
            label="Deductible"
            value={
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${selectedRow?.isDeductible
                  ? "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}
              >
                {selectedRow?.isDeductible ? "Yes" : "No"}
              </span>
            }
          />
        )}
      </div>

      {/* Reason — readable text (not an input-like box). */}
      <div className="mt-4">
        <InfoItem label="Reason" value={selectedRow?.reason || "—"} />
      </div>

      {/* Comment (admin, when present) — readable text. */}
      {selectedRow?.comment && (
        <div className="mt-3">
          <InfoItem label="Comment" value={selectedRow.comment} />
        </div>
      )}
    </div>
  );
};

export default ViewLeave;
